import React, {useEffect, useState} from 'react';
import {toast} from "react-toastify";
import FetchService from "../../../../services/api/FetchService";
import {
    IChecklistManagementItemEditBody,
    IChecklistManagementItemResource,
    TChecklistItemValueType,
} from "../../../../resources/IChecklistManagement.resource";
import {ErrorToastSettings} from "../../../../services/toast/toastSettings";
import ClickCopyBtn from "../../../AutomaticAssessmentPage/components/ClickCopyBtn";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import DeleteIconButton from "../../../../components/DeleteIconButton";
import EditableCell from "../EditableCell";
import styles from './styles.module.scss';

type IDraft = IChecklistManagementItemEditBody;

interface IProps {
    items: IChecklistManagementItemResource[];
    isApproved: boolean;
    companyId: number;
    checklistId: number;
    onItemSaved: (item: IChecklistManagementItemResource) => void;
    onItemAdded: (item: IChecklistManagementItemResource) => void;
    onItemDeleted: (itemUuid: string) => void;
}

const toDraft = (item: IChecklistManagementItemResource): IDraft => ({
    questionType: item.questionType,
    question: item.question,
    example: item.example,
    answer: item.answer,
    valueType: item.valueType,
    weight: item.weight,
});

const isDraftDirty = (item: IChecklistManagementItemResource, draft: IDraft): boolean =>
    draft.questionType !== item.questionType ||
    draft.question !== item.question ||
    draft.example !== item.example ||
    draft.answer !== item.answer ||
    draft.valueType !== item.valueType ||
    draft.weight !== item.weight;

const ChecklistItemsTable: React.FC<IProps> = ({
    items, isApproved, companyId, checklistId, onItemSaved, onItemAdded, onItemDeleted,
}) => {
    const [drafts, setDrafts] = useState<Record<string, IDraft>>({});
    const [savingUuid, setSavingUuid] = useState<string | null>(null);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [deleteTarget, setDeleteTarget] = useState<IChecklistManagementItemResource | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    useEffect(() => {
        setDrafts((prev) => {
            const next: Record<string, IDraft> = {...prev};
            items.forEach((item) => {
                const existing = next[item.itemUuid];
                const isDirty = existing !== undefined && isDraftDirty(item, existing);
                if (!isDirty) {
                    next[item.itemUuid] = toDraft(item);
                }
            });
            return next;
        });
    }, [items]);

    const handleFieldChange = (itemUuid: string, field: 'questionType' | 'question' | 'example' | 'answer', value: string) => {
        setDrafts((prev) => ({...prev, [itemUuid]: {...prev[itemUuid], [field]: value}}));
    };

    const handleWeightChange = (itemUuid: string, value: string) => {
        const parsed = parseInt(value, 10);
        setDrafts((prev) => ({...prev, [itemUuid]: {...prev[itemUuid], weight: Number.isNaN(parsed) ? 0 : parsed}}));
    };

    const persistDraft = async (item: IChecklistManagementItemResource, draft: IDraft) => {
        try {
            setSavingUuid(item.itemUuid);
            const {data} = await FetchService.putChecklistManagementItem(companyId, checklistId, item.itemUuid, draft);
            onItemSaved(data);
        } catch (error) {
            toast.error('Не вдалося зберегти зміни', ErrorToastSettings);
            setDrafts((prev) => ({...prev, [item.itemUuid]: toDraft(item)}));
        } finally {
            setSavingUuid(null);
        }
    };

    const handleFieldBlur = (item: IChecklistManagementItemResource) => {
        const draft = drafts[item.itemUuid];
        if (!draft || !isDraftDirty(item, draft)) return;
        persistDraft(item, draft);
    };

    const handleValueTypeChange = (item: IChecklistManagementItemResource, valueType: TChecklistItemValueType) => {
        const draft: IDraft = {...(drafts[item.itemUuid] ?? toDraft(item)), valueType};
        setDrafts((prev) => ({...prev, [item.itemUuid]: draft}));
        persistDraft(item, draft);
    };

    const handleAddItem = async () => {
        try {
            setIsAdding(true);
            const {data} = await FetchService.postChecklistManagementItem(companyId, checklistId);
            onItemAdded(data);
        } catch (error) {
            toast.error('Не вдалося додати питання', ErrorToastSettings);
        } finally {
            setIsAdding(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        try {
            setIsDeleting(true);
            await FetchService.deleteChecklistManagementItem(companyId, checklistId, deleteTarget.itemUuid);
            onItemDeleted(deleteTarget.itemUuid);
            setDeleteTarget(null);
        } catch (error) {
            toast.error('Не вдалося видалити питання', ErrorToastSettings);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={styles.table__container}>
            <table
                className={styles.table}
                cellSpacing={0}
                cellPadding={0}
            >
                <thead className={styles.table__thead}>
                <tr>
                    <td className={styles.table__cell_first}>UUID</td>
                    <td className={styles.table__cell_head}>Тип</td>
                    <td className={styles.table__cell_head}>Питання</td>
                    <td className={styles.table__cell_head}>Приклад</td>
                    <td className={styles.table__cell_head}>Відповідь</td>
                    <td className={styles.table__cell_head}>Значення</td>
                    <td className={styles.table__cell_head}>Вага</td>
                    <td className={styles.table__cell_last}/>
                </tr>
                </thead>
                <tbody className={styles.table__tbody}>
                {items.map((item) => {
                    const draft = drafts[item.itemUuid] ?? toDraft(item);
                    const isSaving = savingUuid === item.itemUuid;
                    return (
                        <tr key={item.itemUuid}>
                            <td>
                                <ClickCopyBtn text={item.itemUuid}/>
                            </td>
                            <td>
                                <EditableCell
                                    value={draft.questionType}
                                    disabled={isApproved || isSaving}
                                    onChange={(value) => handleFieldChange(item.itemUuid, 'questionType', value)}
                                    onBlur={() => handleFieldBlur(item)}
                                />
                            </td>
                            <td>
                                <EditableCell
                                    value={draft.question}
                                    disabled={isApproved || isSaving}
                                    onChange={(value) => handleFieldChange(item.itemUuid, 'question', value)}
                                    onBlur={() => handleFieldBlur(item)}
                                />
                            </td>
                            <td>
                                <EditableCell
                                    value={draft.example}
                                    disabled={isApproved || isSaving}
                                    onChange={(value) => handleFieldChange(item.itemUuid, 'example', value)}
                                    onBlur={() => handleFieldBlur(item)}
                                />
                            </td>
                            <td>
                                <EditableCell
                                    value={draft.answer}
                                    disabled={isApproved || isSaving}
                                    onChange={(value) => handleFieldChange(item.itemUuid, 'answer', value)}
                                    onBlur={() => handleFieldBlur(item)}
                                />
                            </td>
                            <td>
                                {isApproved ? (
                                    <span>{item.valueType}</span>
                                ) : (
                                    <select
                                        className={styles.table__select}
                                        value={draft.valueType}
                                        disabled={isSaving}
                                        onChange={(e) => handleValueTypeChange(item, e.target.value as TChecklistItemValueType)}
                                    >
                                        <option value={'SHORT'}>SHORT</option>
                                        <option value={'STRING'}>STRING</option>
                                    </select>
                                )}
                            </td>
                            <td>
                                {isApproved ? (
                                    <span>{item.weight}</span>
                                ) : (
                                    <input
                                        type={'number'}
                                        min={1}
                                        className={styles.table__weightInput}
                                        value={draft.weight}
                                        disabled={isSaving}
                                        onChange={(e) => handleWeightChange(item.itemUuid, e.target.value)}
                                        onBlur={() => handleFieldBlur(item)}
                                    />
                                )}
                            </td>
                            <td>
                                {!isApproved ? (
                                    <DeleteIconButton
                                        title={'Видалити питання'}
                                        disabled={isSaving}
                                        onClick={() => setDeleteTarget(item)}
                                    />
                                ) : null}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
            {!isApproved ? (
                <button
                    type={'button'}
                    className={styles.table__addBtn}
                    disabled={isAdding}
                    onClick={handleAddItem}
                >
                    {isAdding ? 'Додавання...' : '+ Додати питання'}
                </button>
            ) : null}
            <ConfirmDialog
                open={deleteTarget !== null}
                title={'Видалити питання?'}
                text={'Цю дію неможливо скасувати. Питання буде видалено з чеклиста.'}
                confirmText={'Видалити'}
                isLoading={isDeleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default ChecklistItemsTable;
